// Профайлер — конфиг полей. Создан как клон resources_booster.ts с
// удалением SERP-специфики (поиск, сайт, умный режим, API заданий) и
// добавлением профайлер-специфики (создание профилей, режимы прогрева,
// Galaxy/Space, Yandex-аккаунт).
//
// Базовые типы (FieldType / VisibleWhen / ResourceDef) держим в resources.ts —
// на них опирается resources_booster.ts. Отсюда импортируем и реэкспортируем,
// чтобы не плодить дубликаты.

import type { FieldType, VisibleWhen, ResourceDef } from './resources_types'
export type { FieldType, VisibleWhen, ResourceDef }

export const PROFILER_SECTIONS = [
  'Основные настройки',
  'Прокси и анонимность',
  'База данных',
  'Прогрев профиля',
  'Опциональные настройки',
] as const

export type ProfilerSectionName = (typeof PROFILER_SECTIONS)[number]

export const PROFILER_RESOURCES: Record<string, ResourceDef> = {
  P0F_TCP_PROFILE: { section: 'Опциональные настройки', description: '🧪 TCP/IP отпечаток (p0f / JA4T)', type: 'Select', defaultValue: '', options: ['', 'iOS (iPhone)', 'Android', 'Свой — JA4T строкой', 'Свой — по параметрам'], adminOnly: true, hint: 'ПОЛНОСТЬЮ переопределяет отпечаток TCP SYN (p0f / JA4T) — порядок опций, window, window scale, MSS, TTL. Можно задать ЛЮБЫЕ значения (даже нереалистичные). Пусто = под REtake iOS, иначе нативный Linux; «iOS (iPhone)» = Darwin (65535/ws6); «Android» = (65535/ws8); «Свой — JA4T строкой» = одной строкой; «Свой — по параметрам» = поля ниже. Только трафик браузера (метка сокета), системный не трогается; с VPN и без. ⚠ Через SOCKS-прокси отпечаток задаёт выходной узел прокси. Требует systemd-сервис browserbig-p0f-ios. После смены — стоп/старт проекта.' },
  P0F_TCP_JA4T: { section: 'Опциональные настройки', description: '🧪 JA4T строкой', type: 'FixedString', defaultValue: '', visibleWhen: { variable: 'P0F_TCP_PROFILE', value: 'Свой — JA4T строкой' }, adminOnly: true, hint: 'Формат JA4T: window_опции_mss_wscale. Опции — kind через дефис: 2=MSS, 1=NOP, 3=WScale, 8=Timestamp, 4=SACK-OK, 0=EOL. Пример iPhone: 65535_2-1-3-1-1-8-4-0_1240_6. MSS=* или 0 → из канала. Эталон — browserleaks.com/ip.' },
  P0F_WINDOW: { section: 'Опциональные настройки', description: '🧪 TCP window size', type: 'FixedString', defaultValue: '', visibleWhen: { variable: 'P0F_TCP_PROFILE', value: 'по параметрам' }, adminOnly: true, hint: 'Размер TCP-окна в SYN (0–65535). ПУСТО = дефолт 65535; явный «0» = ровно ноль (не дефолт). iPhone=65535, Linux≈64240.' },
  P0F_WSCALE: { section: 'Опциональные настройки', description: '🧪 Window Scale', type: 'FixedString', defaultValue: '', visibleWhen: { variable: 'P0F_TCP_PROFILE', value: 'по параметрам' }, adminOnly: true, hint: 'Множитель окна (0–255). ПУСТО = дефолт 6; явный «0» = ровно ноль. iPhone=6, Linux=7, Android=8.' },
  P0F_OPTIONS: { section: 'Опциональные настройки', description: '🧪 Порядок TCP-опций', type: 'FixedString', defaultValue: '', visibleWhen: { variable: 'P0F_TCP_PROFILE', value: 'по параметрам' }, adminOnly: true, hint: 'Имена через запятую ИЛИ kind-числа. ПУСТО = дефолт iPhone (mss,nop,ws,nop,nop,ts,sack,eol). Имена: mss, nop, ws, ts, sack, eol. Linux/Android: mss,sack,ts,nop,ws.' },
  P0F_MSS: { section: 'Опциональные настройки', description: '🧪 MSS', type: 'FixedString', defaultValue: '', visibleWhen: { variable: 'P0F_TCP_PROFILE', value: 'по параметрам' }, adminOnly: true, hint: 'ПУСТО или «0» = из канала (MTU−40). >0 = форсировать.' },
  P0F_TTL: { section: 'Опциональные настройки', description: '🧪 TTL', type: 'FixedString', defaultValue: '', visibleWhen: { variable: 'P0F_TCP_PROFILE', value: 'по параметрам' }, adminOnly: true, hint: 'ПУСТО или «0» = не трогать (ядро, обычно 64). >0 = форсировать (iPhone/Linux=64, Windows=128).' },

  // ═══ Основные настройки ═══

  CNT_THREADS: {
    section: 'Основные настройки', subsection: 'Запуск',
    description: 'Количество потоков',
    type: 'FixedInteger', defaultValue: 49, minValue: 1,
    hint: 'Сколько Chrome-процессов запускать параллельно. Зависит от RAM/CPU сервера. Эмпирически: ~2 GB RAM на поток + 1 ядро на 4 потока.',
  },

  SERVER_REMOTE_STATUS: {
    section: 'Основные настройки', subsection: 'Режим работы',
    description: 'Удаленная работа скрипта',
    type: 'Select', defaultValue: 'Включено',
    options: ['Включено', 'Отключено'],
    hint: 'Включено — скрипт работает с центральной БД профилей (PostgreSQL). Отключено — локальный режим без БД (для тестов).',
  },
  ID_SERVER_REMOTE: {
    section: 'Основные настройки', subsection: 'Режим работы',
    description: 'Идентификатор удаленной работы',
    type: 'FixedString', defaultValue: '',
    visibleWhen: { variable: 'SERVER_REMOTE_STATUS', value: 'Включено' },
    hint: 'ID этого сервера в системе центральной БД. Сообщает воркеру, какому пулу профилей он принадлежит.',
  },
  REMOTE_PROFILES: {
    section: 'Основные настройки', subsection: 'Режим работы',
    description: 'Удаленные профиля',
    type: 'Select', defaultValue: 'Да (База данных)',
    options: ['Да (База данных)', 'Нет (Локально)'],
    visibleWhen: { variable: 'SERVER_REMOTE_STATUS', value: 'Отключено' },
  },
  MODE_WORK: {
    section: 'Основные настройки', subsection: 'Режим работы',
    description: 'Режим работы',
    type: 'Select', defaultValue: 'Создание профилей',
    options: ['Создание профилей', 'Прокачка профилей'],
    visibleWhen: { variable: 'REMOTE_PROFILES', value: 'Нет (Локально)' },
    hint: 'Создание — генерация новых профилей с нуля. Прокачка — повышение уровня уже существующих.',
  },

  MODE_CLOUD_PASSWORD: {
    section: 'Основные настройки', subsection: 'Идентификаторы',
    description: 'Индификатор SPACE GROUP',
    type: 'FixedString', defaultValue: '',
    visibleWhen: { variable: 'ANONIM_MODE', value: 'SPACE GROUP' },
    withHistory: true,
  },
  MODE_CLOUD_PASSWORD_GALAXY: {
    section: 'Основные настройки', subsection: 'Идентификаторы',
    description: 'Идентификатор GALAXY',
    type: 'FixedString', defaultValue: '',
    visibleWhen: { variable: 'ANONIM_MODE', value: 'GALAXY PRO' },
    withHistory: true,
    hint: 'Один идентификатор. Кнопкой «+» можно добавить дополнительные — воркеры распределятся между ними по кругу (round-robin).',
  },
  MODE_CLOUD_PASSWORD_SPACE_PRO: {
    section: 'Основные настройки', subsection: 'Идентификаторы',
    description: 'Индификатор SPACE PRO',
    type: 'FixedString', defaultValue: '',
    visibleWhen: { variable: 'ANONIM_MODE', value: 'SPACE2 PRO' },
    withHistory: true,
  },
  MODE_CLOUD_PASSWORD_PUSHERONE: {
    section: 'Основные настройки', subsection: 'Идентификаторы',
    description: 'Индификатор PUSHER ONE',
    type: 'FixedString', defaultValue: '',
    visibleWhen: { variable: 'ANONIM_MODE', value: 'PUSHER ONE' },
    withHistory: true,
  },
  MODE_CLOUD_PASSWORD_PUSHERTWO: {
    section: 'Основные настройки', subsection: 'Идентификаторы',
    description: 'Индификатор',
    type: 'FixedString', defaultValue: '',
    visibleWhen: { variable: 'ANONIM_MODE', value: 'PUSHER TWO' },
    withHistory: true,
  },
  MODE_CLOUD_PASSWORD_PUSHERTWO_BD: {
    section: 'Основные настройки', subsection: 'Идентификаторы',
    description: 'Источник',
    type: 'FixedString', defaultValue: '',
    visibleWhen: { variable: 'ANONIM_MODE', value: 'PUSHER TWO' },
    withHistory: true,
  },
  MODE_CLOUD_PASSWORD_GROUP: {
    section: 'Основные настройки', subsection: 'Идентификаторы',
    description: 'Группа',
    type: 'FixedString', defaultValue: '',
    visibleWhen: { variable: 'ANONIM_MODE', value: 'PUSHER TWO' },
    withHistory: true,
  },

  DIRECTORY_PROFILES_FOLDER: {
    section: 'Основные настройки', subsection: 'Источники данных',
    description: 'Папка к профилям',
    type: 'FilesFromDirectory', defaultValue: '',
    visibleWhen: { variable: 'REMOTE_PROFILES', value: 'Нет (Локально)' },
  },
  FP_CLOUD_MODE: {
    section: 'Основные настройки', subsection: 'Источники данных',
    description: 'Источник фингерпринтов',
    type: 'Select', defaultValue: 'Локальный',
    options: ['Локальный', 'Облачный'],
    visibleWhen: { variable: 'ANONIM_MODE', value: 'SPACE GROUP,SPACE PRO 2,Индификатор' },
  },
  DIRECTORY_FINGERS_FOLDER: {
    section: 'Основные настройки', subsection: 'Источники данных',
    description: 'Папка с фингерпринтами',
    type: 'FilesFromDirectory', defaultValue: '',
    visibleWhen: { variable: 'MODE_WORK', value: 'Создание' },
  },
  UIDS: {
    section: 'Основные настройки', subsection: 'Источники данных',
    description: 'Путь к индификаторам',
    type: 'LinesFromFile', defaultValue: '',
    visibleWhen: { variable: 'ANONIM_MODE', value: 'Индификатор' },
  },
  DIRECTORY_COOKIES_FOLDER: {
    section: 'Основные настройки', subsection: 'Источники данных',
    description: 'Папка с Cookies',
    type: 'FilesFromDirectory', defaultValue: '',
    visibleWhen: { variable: 'ANONIM_MODE', value: 'Cookies' },
  },

  NUMBER_CREATE: {
    section: 'Основные настройки', subsection: 'Создание профилей',
    description: 'Сколько профилей создать',
    type: 'FixedInteger', defaultValue: 1000,
    visibleWhen: { variable: 'MODE_WORK', value: 'Создание' },
  },
  FINGER_DELETE: {
    section: 'Основные настройки', subsection: 'Создание профилей',
    description: 'Удалять использованный фингер',
    type: 'Select', defaultValue: '',
    options: ['Да', ''],
    visibleWhen: { variable: 'MODE_WORK', value: 'Создание' },
    hint: 'Если включено — после применения фингерпринта файл .json удаляется. Защита от повторного использования.',
  },
  STATUS_BOOST_PROFILE: {
    section: 'Основные настройки', subsection: 'Прокачка по дням',
    description: 'Прокачивать по дням',
    type: 'Select', defaultValue: '',
    options: ['Да', ''],
    visibleWhen: { variable: 'MODE_WORK', value: 'Прокачка профилей' },
  },
  NUMBER_BOOST_PROFILE: {
    section: 'Основные настройки', subsection: 'Прокачка по дням',
    description: 'Сколько дней прокачивать',
    type: 'FixedInteger', defaultValue: 6,
    visibleWhen: { variable: 'STATUS_BOOST_PROFILE', value: 'Да' },
  },
  CREATE_CHANCE: {
    section: 'Основные настройки', subsection: 'Прокачка по дням',
    description: 'Шанс создания нового профиля',
    type: 'FixedInteger', defaultValue: 0,
    visibleWhen: { variable: 'MODE_WORK', value: 'Прокачка' },
    hint: 'Процент 0–100. Иногда вместо прокачки старого профиля воркер сделает новый — поддерживает приток "молодых" профилей в пул.',
  },
  KEY_RUCAPCTHA: {
    section: 'Основные настройки', subsection: 'Капча',
    description: 'API ключ cap.cr33p.ru',
    type: 'FixedString', defaultValue: '',
    visibleWhen: { variable: 'MODE_WORK', value: 'Решать' },
    hint: 'Ключ нашего внутреннего сервиса решения капчи Yandex SmartCaptcha.',
  },
  CLEAR_CAPTCHA_IDENTIFIKATOR: {
    section: 'Основные настройки', subsection: 'Капча',
    description: 'Чистить куки капчи (spravka/sq)',
    type: 'Select', defaultValue: 'Нет',
    options: ['Нет', 'Да'],
    hint: 'Да — перед навигацией удаляются куки-идентификаторы капчи (spravka, sq). Обычно не требуется.',
  },

  FILTER_SERVER_TYPE: {
    section: 'Основные настройки', subsection: 'Фильтрация профилей',
    description: 'Server Source',
    type: 'Select', defaultValue: 'Server 1',
    options: ['Server 1', 'Server 2'],
    visibleWhen: { variable: 'ANONIM_MODE', value: 'PUSHER TWO' },
    hint: 'Для GALAXY PRO сервер определяется автоматически через API',
  },
  ANONIM_MODE_COOKIE_UPDATE: {
    section: 'Основные настройки', subsection: 'Фильтрация профилей',
    description: 'Обновлять куки при работе',
    type: 'Select', defaultValue: '',
    options: ['Да', ''],
    visibleWhen: { variable: 'ANONIM_MODE', value: 'Cookie' },
    hint: 'После успешной работы обновляет сохранённые cookies профиля — повышает живучесть.',
  },
  CHECK_VISITED_SITE: {
    section: 'Основные настройки', subsection: 'Фильтрация профилей',
    description: 'Проверять history_profile',
    type: 'Select', defaultValue: '',
    options: ['Да', ''],
    visibleWhen: { variable: 'MODE_WORK', value: 'Использование' },
    hint: 'Проверяет файл history_profile на уже посещённые сайты и пропускает их, чтобы не повторяться.',
  },
  TRANSFER_VISITED_PRODILE: {
    section: 'Основные настройки', subsection: 'Фильтрация профилей',
    description: 'Перенос отработки профиля',
    type: 'Select', defaultValue: 'Нет, оставить в текущей папке',
    options: ['Нет, оставить в текущей папке', 'Да, в папку Worked', 'Удалить профиль'],
    visibleWhen: { variable: 'CHECK_VISITED_SITE', value: 'Да' },
  },
  ANONIME_MODE_GALAXY_FOLDER: {
    section: 'Основные настройки', subsection: 'Фильтрация профилей',
    description: 'Активировать группировку профилей',
    type: 'Select', defaultValue: '',
    options: ['Да', ''],
    visibleWhen: { variable: 'ANONIM_MODE', value: 'GALAXY PRO' },
    hint: 'Группирует профили Galaxy по папкам на диске — удобно для ручного управления.',
  },
  ANONIME_MODE_GALAXY_FOLDER_NAME: {
    section: 'Основные настройки', subsection: 'Фильтрация профилей',
    description: 'Имя группы GALAXY',
    type: 'FixedString', defaultValue: '',
    visibleWhen: { variable: 'ANONIME_MODE_GALAXY_FOLDER', value: 'Да' },
  },

  MODE_USED_PROFILES: {
    section: 'Основные настройки', subsection: 'Контроль использований',
    description: 'Контролировать использования',
    type: 'Select', defaultValue: '',
    options: ['Да', ''],
    visibleWhen: { variable: 'ANONIM_MODE', value: 'PUSHER TWO' },
    hint: 'Ведёт учёт использований каждого профиля и применяет ограничения (например, не чаще 1 раза в день).',
  },
  MODE_USED_PROFILES_NUMBER: {
    section: 'Основные настройки', subsection: 'Контроль использований',
    description: 'Лимит использований',
    type: 'FixedInteger', defaultValue: 0,
    visibleWhen: { variable: 'MODE_USED_PROFILES', value: 'Да' },
  },
  MODE_USED_PROFILES_TYPE: {
    section: 'Основные настройки', subsection: 'Контроль использований',
    description: 'Режим контроля',
    type: 'Select', defaultValue: 'Архивация',
    options: ['Архивация', 'Удаление'],
    visibleWhen: { variable: 'MODE_USED_PROFILES', value: 'Да' },
  },
  MODE_USED_PROFILES_GROUP: {
    section: 'Основные настройки', subsection: 'Контроль использований',
    description: 'Архивная группа',
    type: 'FixedString', defaultValue: '',
    visibleWhen: { variable: 'MODE_USED_PROFILES_TYPE', value: 'Архивация' },
  },
  // Удалены SERP-специфичные поля (SITE_NOT_FOUND, GALAXY_REPEAT_CLICK*)
  // — они про повторный клик одним профилем по одному сайту в SERP,
  // профайлеру для прогрева не нужно.
  _SERP_REPEAT_PLACEHOLDER: {
    section: '_hidden_removed', subsection: 'hidden',
    description: '', type: 'FixedString', defaultValue: '',
  },

  // ═══ Прокси и анонимность ═══

  PROXY_USE: {
    section: 'Прокси и анонимность', subsection: 'Подключение',
    description: 'Использовать прокси',
    type: 'Select', defaultValue: '',
    options: ['Да', ''],
    visibleWhen: { variable: 'SERVER_REMOTE_STATUS', value: 'Отключено' },
    hint: 'Если выключено — Chrome ходит напрямую с IP сервера. Yandex быстро забанит, если несколько профилей идут с одного IP.',
  },
  PROXY_FORMAT: {
    section: 'Прокси и анонимность', subsection: 'Подключение',
    description: 'Формат прокси',
    type: 'Select', defaultValue: 'HTTP(S)',
    options: ['HTTP(S)', 'SOCKS5'],
    visibleWhen: { variable: 'PROXY_USE', value: 'Да' },
  },
  PROXY_TYPE: {
    section: 'Прокси и анонимность', subsection: 'Источник',
    description: 'Тип использования',
    type: 'Select', defaultValue: 'Из файла',
    options: ['Из файла', 'По ссылке', 'Конкретное'],
    visibleWhen: { variable: 'PROXY_USE', value: 'Да' },
    hint: 'Из файла — статический список. По ссылке — API провайдера (получаем по запросу). Конкретное — одна прокся для всех воркеров.',
  },
  PROXY_URL: {
    section: 'Прокси и анонимность', subsection: 'Источник',
    description: 'Индификатор проксей',
    type: 'FixedString', defaultValue: '',
    visibleWhen: { variable: 'PROXY_TYPE', value: 'Облачные' },
  },
  PROXY_IGNORE_PROFILE: {
    section: 'Прокси и анонимность', subsection: 'Источник',
    description: 'Игнорировать прокси профиля',
    type: 'Select', defaultValue: '',
    options: ['', 'Да'],
    visibleWhen: { variable: 'PROXY_USE', value: 'Да' },
    hint: 'Тестовый режим. Обычно за профилем закрепляется прокси, через который его прокачивали, '
      + 'и при следующей прокачке берётся именно он. Включите, чтобы принудительно брать прокси из '
      + 'настроек проекта (источник выше), игнорируя сохранённый. Привязка профиля при этом НЕ '
      + 'перезаписывается — после теста профиль вернётся к своему прокси.',
  },
  PROXY_MIND_LINK: {
    section: 'Прокси и анонимность', subsection: 'Источник',
    description: 'Ссылка на получение прокси',
    type: 'FixedString', defaultValue: '',
    visibleWhen: { variable: 'PROXY_TYPE', value: 'По ссылке' },
    hint: 'Вам необходимо поднять свое API, которое будет отдавать прокси в формате IP:PORT:USER:PASS (только 1 строка), каждый Get запрос должен выдавать новую прокси.',
  },
  PROXY_LIST: {
    section: 'Прокси и анонимность', subsection: 'Источник',
    description: 'Файл со списком прокси',
    type: 'LinesFromFile', defaultValue: '',
    visibleWhen: { variable: 'PROXY_TYPE', value: 'Из файла' },
  },
  PROXY_SHUFFLE: {
    section: 'Прокси и анонимность', subsection: 'Источник',
    description: 'Перемешать прокси',
    type: 'Select', defaultValue: '',
    options: ['Да', ''],
    visibleWhen: { variable: 'PROXY_TYPE', value: 'Из файла' },
    hint: 'Перемешивает список прокси при загрузке, чтобы соседние потоки не шли подряд с одного IP.',
  },
  PROXY_IPPORT: {
    section: 'Прокси и анонимность', subsection: 'Источник',
    description: 'ip:port(:login:pass:updateUrl)',
    type: 'FixedString', defaultValue: '',
    visibleWhen: { variable: 'PROXY_TYPE', value: 'Конкретное' },
  },

  // ═══ База данных ═══

  BD_NAME: {
    section: 'База данных', subsection: 'Подключение',
    description: 'Индификатор базы данных',
    type: 'FixedString', defaultValue: '',
    visibleWhen: { variable: 'REMOTE_PROFILES', value: 'Да (База данных)' },
    hint: 'Имя базы профилей в PostgreSQL — обычно совпадает с проектом (galaxy_pro / space_pro).',
  },
  PARTY_PROFILE_NAME: {
    section: 'База данных', subsection: 'Подключение',
    description: 'Группа хранения профилей',
    type: 'FixedString', defaultValue: '',
    visibleWhen: { variable: 'REMOTE_PROFILES', value: 'Да' },
    hint: 'Подгруппа внутри БД (party). Позволяет одной базе хранить несколько изолированных пулов.',
  },
  BD_SERVER: {
    section: 'База данных', subsection: 'Подключение',
    description: 'Сервер БД профилей (для локального режима)',
    type: 'Select', defaultValue: '',
    options: ['Сервер #1', 'Сервер #2'],
    visibleWhen: { variable: 'SERVER_REMOTE_STATUS', value: 'Отключено' },
    hint: 'На каком PostgreSQL-сервере лежат профили в локальном режиме. Пусто или «Сервер #1» — server1; «Сервер #2» — server2.',
  },
  GET_PID_SOURCE: {
    section: 'База данных', subsection: 'Подключение',
    description: 'Источник PID профиля',
    type: 'Select', defaultValue: 'Нет',
    options: ['Нет', 'Да'],
    visibleWhen: { variable: 'REMOTE_PROFILES', value: 'Да (База данных)' },
    hint: 'Нет — профиль берётся напрямую из PostgreSQL (claim). Да — PID запрашивается через API get.php, с fallback на БД (как в BAS BD_ModeWork).',
  },
  STATUS_CTREATE_NEW_PROFILE: {
    section: 'База данных', subsection: 'Создание новых',
    description: 'Создавать новые профили?',
    type: 'Select', defaultValue: '',
    options: ['Да', ''],
    visibleWhen: { variable: 'REMOTE_PROFILES', value: 'Да' },
    hint: 'Если выключено — воркер только прокачивает существующие профили из БД и не добавляет новых.',
  },
  LIMIT_CREATE_PROFILE: {
    section: 'База данных', subsection: 'Создание новых',
    description: 'Целевой размер пула',
    type: 'FixedInteger', defaultValue: 1000,
    visibleWhen: { variable: 'STATUS_CTREATE_NEW_PROFILE', value: 'Да' },
    hint: 'Целевое количество профилей в группе (потолок пула). Когда оно достигнуто — воркер перестаёт создавать новые и только прокачивает. Суточная квота создания выводится автоматически = размер пула / 3 (3-дневный цикл L1→L2→L3), отдельно её задавать не нужно.',
  },

  // ═══ Антидетект ═══

  CHECKBOX_METOD: {
    section: 'Антидетект', subsection: 'Методы решения',
    description: 'Метод решения PoW',
    type: 'Select', defaultValue: 'Нативный',
    options: ['Нативный', 'Быстрый'],
    hint: 'Нативный — проходит все типы капч (чекбокс, слайдер, картинка), более бережный режим. Быстрый — режим ускоренной обработки; иногда помогает на устойчивых сайтах, но часть капч может не пройти.',
  },
  TLS_PROFILE_SAFARI_IOS: {
    section: 'Антидетект', subsection: 'Сетевые параметры',
    description: 'REtake Hello',
    type: 'Select', defaultValue: '',
    options: ['Да', ''],
    hint: 'Воркер первым же подключением представляется сайту мобильным устройством. Антибот видит обычный смартфон, а не browser-фарму. Пробивает большинство современных проверок «на лету», без подмены user-agent. Отключает остальные сетевые опции — они либо ломают, либо дублируют этот режим.',
  },
  TLS_RANDOMIZE: {
    section: 'Антидетект', subsection: 'Сетевые параметры',
    description: 'Затеряться среди толпы (TLS MIX)',
    type: 'Select', defaultValue: 'Да',
    options: ['Да', ''],
    hint: 'Усиливает рандомизацию параметров подключения: каждый воркер получает свой уникальный набор сетевых настроек. Сайт не объединяет их в один поток и относится к каждому как к новому посетителю. Выключать почти никогда не нужно.',
  },
  TLS_UPDATER: {
    section: 'Антидетект', subsection: 'Сетевые параметры',
    description: 'Стать стариком (ALPS)',
    type: 'Select', defaultValue: '',
    options: ['Да', ''],
    hint: 'Воркер использует более старую схему подключения. Помогает на сайтах, которые ослабляют защиту для устаревших клиентов и пропускают их с меньшим скепсисом.',
  },
  TLS_UNIQUE_EXT: {
    section: 'Антидетект', subsection: 'Сетевые параметры',
    description: 'Стать уникальным (UNIQ)',
    type: 'Select', defaultValue: '',
    options: ['Да', ''],
    hint: 'Каждый воркер получает индивидуальный отпечаток подключения, которого нет в базах известных ботов. Помогает обойти антиботы, сверяющие отпечатки со своими базами.',
  },
  REDIRCNT_MODE: {
    section: 'Антидетект', subsection: 'Дополнительные опции',
    description: 'Redircnt режим',
    type: 'Select', defaultValue: '',
    options: ['Да', ''],
    adminOnly: true,
    hint: 'Переключает SERP-навигацию на /yandsearchl с referrer yandex.ru/search (ранее — anticap + nickpusher228).',
  },
  RETAKE_MODE: {
    section: 'Антидетект', subsection: 'Дополнительные опции',
    description: 'REtake',
    type: 'Select', defaultValue: '',
    options: ['Да', ''],
    hint: 'Альтернативный режим подачи трафика. Меняет способ, которым браузер представляется сайту, не трогая основной отпечаток. Включать точечно — если остальные режимы перестали пробивать конкретный сайт.',
  },

  // ═══ Прогрев профиля ═══

  STATUS_BOOST_MODE: {
    section: 'Прогрев профиля', subsection: 'Основное',
    description: 'Активировать прогрев профиля',
    type: 'Select', defaultValue: '',
    options: ['Да', ''],
    visibleWhen: { variable: 'SERVER_REMOTE_STATUS', value: 'Отключено' },
    hint: 'Главный switch для всей секции прогрева. Отключи если хочешь только создавать профили без прокачки.',
  },
  MODE_PROGREV_TYPE: {
    section: 'Прогрев профиля', subsection: 'Быстрый прогрев',
    description: 'Тип прогрева YA',
    type: 'Select', defaultValue: 'Serp Tune',
    options: ['Serp Tune', 'Serp Tune Plus', 'Ads Tune', 'Ads Tune Plus', 'Redircnt Tune', 'Direct Tune', 'Video Tune', 'Entity Tune', 'Entity Word Tune', 'Mix Tune'],
    hint: 'Режим прогрева профиля. Имена режимов из BAS — каждый исполняет свой сценарий прокачки.',
  },
  CREATE_DAILY_QUOTA: {
    section: 'База данных', subsection: 'Основное',
    description: 'Суточная квота создания',
    type: 'FixedInteger', defaultValue: 0,
    visibleWhen: { variable: 'STATUS_CTREATE_NEW_PROFILE', value: 'Да' },
    hint: 'Сколько профилей создавать за сутки. 0 = автоматически треть от «Лимита профилей» '
      + '(пул наполняется за 3 дня, ровно под цикл L1→L2→L3). '
      + '-1 = без суточного ограничения: создаём сразу столько, сколько задано в лимите. '
      + 'Любое число больше нуля — своя квота в сутки.',
  },
  FILTER_PROFILE_GALAXY: {
    section: 'База данных', subsection: 'Основное',
    description: 'Фильтровать по использованиям (Galaxy)',
    type: 'Select', defaultValue: '',
    options: ['', 'Да'],
    hint: 'Galaxy-пулы: брать не любой свободный профиль, а с учётом того, использовался ли он. '
      + 'Метод выбирается ниже. Выключено — берётся любой свободный. Точечно переопределить можно '
      + 'параметром «Фильтр по использованию» у кубика «Получить профиль» в редакторе сценария.',
  },
  FILTER_PROFILE_GALAXY_MODE: {
    section: 'База данных', subsection: 'Основное',
    description: 'Метод фильтрации',
    type: 'Select', defaultValue: 'Использовать уникальные профили',
    options: ['Использовать уникальные профили', 'Использовать отработанные профили'],
    visibleWhen: { variable: 'FILTER_PROFILE_GALAXY', value: 'Да' },
    hint: 'Уникальные — только ещё не использованные (used=0); если таких нет, бронь честно откажет. '
      + 'Отработанные — уже работавшие по домену (для повторного клика).',
  },
  PROFILE_CLAIM_AGE_GATE: {
    section: 'База данных', subsection: 'Основное',
    description: 'Брать только профили старше суток',
    type: 'Select', defaultValue: '',
    options: ['', 'Да'],
    hint: 'Возрастной фильтр самой брони — не путать с «Выдержкой перед прокачкой». Да — профиль '
      + 'моложе суток не берётся вообще. По умолчанию выключено (как в BAS). Можно переопределить '
      + 'параметром кубика «Получить профиль», в том числе принудительно выключить значением «Нет».',
  },
  WARMING_LEVEL_CAP: {
    section: 'Прогрев профиля', subsection: 'Основное',
    description: 'Потолок уровня прокачки',
    type: 'FixedInteger', defaultValue: 0,
    hint: 'До какого уровня качать профиль. По умолчанию 0 — уровневых ограничений НЕТ: качаем весь пул '
      + 'независимо от level, профиль возвращается в очередь через 3 минуты. Так работают боевые '
      + 'Galaxy-пулы: их наполняет внешний конвейер, уровни там произвольные (1..8 и выше), '
      + 'и потолок отсекал бы почти всё. '
      + '3 = трёхдневная модель L1→L2→L3 (после L3 профиль готов и уходит бустеру). '
      + '999 — потолок и возрастное созревание снимаются, но правило «одна успешная прокачка в сутки» остаётся.',
  },
  WARMING_AGE_HOURS: {
    section: 'Прогрев профиля', subsection: 'Основное',
    description: 'Выдержка перед прокачкой, часов на уровень',
    type: 'FixedInteger', defaultValue: 0,
    hint: 'Сколько профиль должен «отлежаться» перед следующей прокачкой. По умолчанию 0 — выдержки нет, '
      + 'профиль доступен сразу после создания. Условие умножается на level, то есть само по себе '
      + 'уровневое, и на пуле, набитом залпом (30 тыс. профилей за день), оно отсекало сразу весь пул. '
      + '24 = сутки на уровень (L1 через 24ч после создания, L2 через 48ч) — базовая трёхдневная модель, '
      + 'включать вместе с потолком уровня 3.',
  },
  Tegs: {
    section: 'Прогрев профиля', subsection: 'Умный серфинг',
    description: 'Ключевые слова Yandex',
    type: 'LinesFromFile', defaultValue: '',
    visibleWhen: { variable: 'BOOST_TYPE_SERFING', value: 'Поиск по ключевым словам в Яндекс,Поиск по ключевым словам Google,Рандомный выбор из трёх' },
  },
  BOOST_TYPE_URL_MODE: {
    section: 'Прогрев профиля', subsection: 'Прямой серфинг',
    description: 'Настройки прямого прогрева',
    type: 'Select', defaultValue: '',
    options: ['Да', ''],
    visibleWhen: { variable: 'STATUS_BOOST_MODE', value: 'БРА' },
  },
  BOOST_TYPE_URL_REGION: {
    section: 'Прогрев профиля', subsection: 'Прямой серфинг',
    description: 'Регион сайтов (lr)',
    type: 'FixedString', defaultValue: '213',
    visibleWhen: { variable: 'BOOST_TYPE_URL_MODE', value: 'Да' },
    hint: 'Yandex LR-код региона (213 = Москва, 2 = СПб, 11 = Казань…). Влияет на ранжирование выдачи.',
  },
  BOOST_TYPE_URL_DI: {
    section: 'Прогрев профиля', subsection: 'Прямой серфинг',
    description: 'Категории интересов',
    type: 'FixedString', defaultValue: '0',
    visibleWhen: { variable: 'BOOST_TYPE_URL_MODE', value: 'Да' },
  },
  BOOST_URLS_HISTORY: {
    section: 'Прогрев профиля', subsection: 'Прямой серфинг',
    description: 'Сайты для прокачки',
    type: 'LinesFromFile', defaultValue: '',
    visibleWhen: { variable: 'STATUS_BOOST_MODE', value: 'Нет' },
  },
  BOOST_NUMBER_URLS: {
    section: 'Прогрев профиля', subsection: 'Прямой серфинг',
    description: 'Количество посещенных сайтов',
    type: 'RandomInteger', defaultValue: '1-30', minValue: 1, maxValue: 30,
    visibleWhen: { variable: 'STATUS_BOOST_MODE', value: 'Нет' },
  },
  Min_Time_Serf: {
    section: 'Прогрев профиля', subsection: 'Тайминги',
    description: 'Минимальное время прогрева',
    type: 'FixedInteger', defaultValue: 120,
    visibleWhen: { variable: 'MODE_PROGREV', value: 'Умное' },
  },
  Max_Time_Serf: {
    section: 'Прогрев профиля', subsection: 'Тайминги',
    description: 'Максимальное время прогрева',
    type: 'FixedInteger', defaultValue: 240,
    visibleWhen: { variable: 'MODE_PROGREV', value: 'Умное' },
  },
  BOOST_TIMER_SESSION: {
    section: 'Прогрев профиля', subsection: 'Тайминги',
    description: 'Время всей сессии (сек)',
    type: 'FixedInteger', defaultValue: 600,
    visibleWhen: { variable: 'STATUS_BOOST_MODE', value: 'Нет' },
  },
  BOOST_TIMER_URL: {
    section: 'Прогрев профиля', subsection: 'Тайминги',
    description: 'Время на 1 сайте (сек)',
    type: 'RandomInteger', defaultValue: '60-300', minValue: 60, maxValue: 300,
    visibleWhen: { variable: 'STATUS_BOOST_MODE', value: 'Нет' },
  },
  BOOST_TIMEOUT_OPEN_URL: {
    section: 'Прогрев профиля', subsection: 'Тайминги',
    description: 'Таймаут открытия сайта (сек)',
    type: 'FixedInteger', defaultValue: 10,
    visibleWhen: { variable: 'STATUS_BOOST_MODE', value: 'Нет' },
  },
  PROGREV_INTERES: {
    section: 'Прогрев профиля', subsection: 'Умный серфинг',
    description: 'Источник сайтов для серфинга',
    type: 'Select', defaultValue: '',
    options: ['Cr33p API', 'BK API', ''],
    visibleWhen: { variable: 'STATUS_BOOST_MODE', value: 'Да' },
    hint: 'Cr33p API — серфинг по внешнему списку сайтов (BAS BD_ModeWork): профиль заходит на реальные сайты через подмену гео-ссылки выдачи. Остальные значения — без серфинга по внешнему источнику (базовое поведение).',
  },
  BOOST_TYPE_SERFING: {
    section: 'Прогрев профиля', subsection: 'Умный серфинг',
    description: 'Тип серфинга',
    type: 'Select', defaultValue: 'Общий серфинг (прямые заходы)',
    options: ['Общий серфинг (прямые заходы)', 'Поиск по ключевым словам в Яндекс', 'Поиск по ключевым словам Google', 'Рандомный выбор из трёх', ''],
    visibleWhen: { variable: 'STATUS_BOOST_MODE', value: 'Да' },
    hint: 'Стратегия серфинга профиля. «Общий серфинг (прямые заходы)» — прямые заходы на сайты источника; режимы с ключевыми словами активируют список «Ключевые слова Yandex».',
  },
  TIME_ON_SITE: {
    section: 'Прогрев профиля', subsection: 'Тайминги',
    description: 'Время на сайте при серфинге (сек)',
    type: 'RandomInteger', defaultValue: '60-120', minValue: 60, maxValue: 120,
    visibleWhen: { variable: 'STATUS_BOOST_MODE', value: 'Да' },
    hint: 'Диапазон удержания на каждом сайте при серфинге (TIME_ON_SITE_MIN/MAX). Используется веткой Cr33p API в Serfing.',
  },
  MAX_SITE_SERFING: {
    section: 'Прогрев профиля', subsection: 'Умный серфинг',
    description: 'Сайтов за сессию серфинга',
    type: 'RandomInteger', defaultValue: '3-7', minValue: 1, maxValue: 50,
    visibleWhen: { variable: 'STATUS_BOOST_MODE', value: 'Да' },
    hint: 'Диапазон количества сайтов, посещаемых за одну сессию серфинга (MAX_SITE_SERFING_MIN/MAX).',
  },
  MAX_LINKED_BOOST: {
    section: 'Прогрев профиля', subsection: 'Умный серфинг',
    description: 'Внутренних переходов на сайте',
    type: 'RandomInteger', defaultValue: '1-2', minValue: 0, maxValue: 20,
    visibleWhen: { variable: 'STATUS_BOOST_MODE', value: 'Да' },
    hint: 'Диапазон числа внутренних переходов (по ссылкам) на каждом посещённом сайте (MAX_LINKED_BOOST_MIN/MAX).',
  },

  // ═══ Яндекс аккаунт ═══

  // ═══ Уведомления Telegram ═══

  // ═══ Опциональные настройки ═══

  HAND_CONTROL: {
    section: 'Опциональные настройки', subsection: 'Управление',
    description: 'Ручное управление',
    type: 'Select', defaultValue: '',
    options: ['Да', ''],
    hint: 'Включает ручное управление в точке, где в сценарии стоит кубик «Ручное управление (пауза VNC)»: на этом кубике поток отдаёт браузер вам по VNC, затем продолжает сценарий. Без кубика в сценарии тумблер ничего не делает.',
  },
  ADS_CONTEXT_STUB: {
    section: 'Опциональные настройки', subsection: 'Антидетект',
    description: 'Заглушка рекламного лоадера (context.js)',
    type: 'Select', defaultValue: 'Нет',
    options: ['Да', 'Нет'],
    hint: 'Вместо тела рекламного лоадера Яндекса (~428 КБ) отдаётся пустой скрипт. Загрузка при этом считается успешной, поэтому антиадблок видит вердикт NOT_BLOCKED и ставит куку cycada, а сам код не исполняется. На прогревочном (пустом) запросе рекламных блоков нет, поэтому наблюдаемых отличий не возникает. «Нет» — грузить настоящий лоадер (нужно, если на выдаче есть рекламные слоты).',
  },
  MODE_SET_FP: {
    section: 'Опциональные настройки', subsection: 'Отпечаток',
    description: 'Настройка отпечатка',
    type: 'Select', defaultValue: 'Стандартные параметры',
    options: ['Стандартные параметры', 'Кастомные параметры'],
    visibleWhen: { variable: 'MODE_WORK', value: 'Создание' },
    hint: 'Стандарт — все защиты включены. Кастом — точечная настройка каждой подделки FP (для отладки).',
  },
  SET_FP_PCANVAS: {
    section: 'Опциональные настройки', subsection: 'Подделки FP',
    description: 'SET_FP_PCANVAS',
    type: 'Select', defaultValue: 'true',
    options: ['true', 'false'],
    visibleWhen: { variable: 'MODE_SET_FP', value: 'Кастомные' },
    hint: 'P-Canvas (paint canvas) noise injection — рандомизирует пиксельный отпечаток отрисованного текста.',
  },
  SET_FP_CANVAS: {
    section: 'Опциональные настройки', subsection: 'Подделки FP',
    description: 'SET_FP_CANVAS',
    type: 'Select', defaultValue: 'true',
    options: ['true', 'false'],
    visibleWhen: { variable: 'MODE_SET_FP', value: 'Кастомные' },
    hint: 'Подмена canvas fingerprint — добавляет шум в getImageData/toDataURL.',
  },
  SET_FP_WEBGL: {
    section: 'Опциональные настройки', subsection: 'Подделки FP',
    description: 'SET_FP_WEBGL',
    type: 'Select', defaultValue: 'true',
    options: ['true', 'false'],
    visibleWhen: { variable: 'MODE_SET_FP', value: 'Кастомные' },
    hint: 'WebGL renderer/vendor spoof: подменяет название GPU на профильное (ARM Mali / Apple).',
  },
  SET_FP_AUDIO: {
    section: 'Опциональные настройки', subsection: 'Подделки FP',
    description: 'SET_FP_AUDIO',
    type: 'Select', defaultValue: 'true',
    options: ['true', 'false'],
    visibleWhen: { variable: 'MODE_SET_FP', value: 'Кастомные' },
    hint: 'AudioContext fingerprint noise — мутирует частотные коэффициенты OscillatorNode.',
  },
  SET_FP_BATTERY: {
    section: 'Опциональные настройки', subsection: 'Подделки FP',
    description: 'SET_FP_BATTERY',
    type: 'Select', defaultValue: 'true',
    options: ['true', 'false'],
    visibleWhen: { variable: 'MODE_SET_FP', value: 'Кастомные' },
    hint: 'Подмена Battery API: реалистичный заряд, скорость зарядки/разрядки.',
  },
  SET_FP_CLIENTRECT: {
    section: 'Опциональные настройки', subsection: 'Подделки FP',
    description: 'SET_FP_CLIENTRECT',
    type: 'Select', defaultValue: 'true',
    options: ['true', 'false'],
    visibleWhen: { variable: 'MODE_SET_FP', value: 'Кастомные' },
    hint: 'getBoundingClientRect noise — субпиксельные сдвиги координат элементов.',
  },
  SET_FP_MOTION: {
    section: 'Опциональные настройки', subsection: 'Подделки FP',
    description: 'SET_FP_MOTION',
    type: 'Select', defaultValue: 'true',
    options: ['true', 'false'],
    visibleWhen: { variable: 'MODE_SET_FP', value: 'Кастомные' },
    hint: 'DeviceMotion события: имитирует естественные колебания телефона в руке.',
  },

  // ═══ Баг-Чекер ═══
}

export function getProfilerFieldsForSection(
  section: ProfilerSectionName,
  isAdmin = false,
): [string, ResourceDef][] {
  return Object.entries(PROFILER_RESOURCES).filter(([, def]) => {
    if (def.section !== section) return false
    if (def.adminOnly && !isAdmin) return false
    return true
  })
}

function resolveValue(variable: string, cfg: Record<string, any>): string {
  if (variable in cfg) return String(cfg[variable] ?? '')
  const varDef = PROFILER_RESOURCES[variable]
  if (varDef) return String(varDef.defaultValue ?? '')
  return ''
}

// В удалённом режиме (SERVER_REMOTE_STATUS=Включено) все настройки прокачки
// приходят с сервера по идентификатору (один GET на settings.php при старте),
// поэтому оператору доступны только эти поля. Остальные скрыты — как в BAS,
// где при «Удаленная работа = Да» оставалось одно поле «Идентификатор».
const REMOTE_VISIBLE_FIELDS = new Set([
  'CNT_THREADS',
  'SERVER_REMOTE_STATUS',
  'ID_SERVER_REMOTE',
])

export function isProfilerFieldVisible(
  key: string,
  cfg: Record<string, any>,
  _seen?: Set<string>,
): boolean {
  const def = PROFILER_RESOURCES[key]

  // Удалённый режим: показываем только идентификатор + количество потоков.
  if (resolveValue('SERVER_REMOTE_STATUS', cfg) === 'Включено') {
    return REMOTE_VISIBLE_FIELDS.has(key)
  }

  // Safari iOS TLS profile maskirует все остальные TLS-tweaks.
  if (key === 'TLS_RANDOMIZE' || key === 'TLS_UPDATER' || key === 'TLS_UNIQUE_EXT') {
    if (resolveValue('TLS_PROFILE_SAFARI_IOS', cfg) === 'Да') return false
  }

  if (!def?.visibleWhen) return true
  const { variable, value } = def.visibleWhen
  const seen = _seen ?? new Set<string>()
  if (seen.has(key)) return false
  seen.add(key)
  if (PROFILER_RESOURCES[variable] && !isProfilerFieldVisible(variable, cfg, seen)) return false
  const current = resolveValue(variable, cfg)
  return value.split(',').some(v => current.includes(v.trim()))
}

export function getProfilerDefaults(): Record<string, any> {
  const defaults: Record<string, any> = {}
  for (const [key, def] of Object.entries(PROFILER_RESOURCES)) {
    if (def.defaultValue === undefined || def.defaultValue === '') continue
    defaults[key] = def.defaultValue
    // RandomInteger'ы сценарий читает как FOO_MIN / FOO_MAX (напр.
    // [[BOOST_NUMBER_URLS_MIN]], [[TIME_ON_SITE_MAX]]). Дефолт хранится
    // строкой "min-max" — раскрываем в отдельные ключи, иначе числовая
    // ветка получает пустую строку и падает арифметика.
    if ((def as any).type === 'RandomInteger' && typeof def.defaultValue === 'string') {
      const m = String(def.defaultValue).match(/^\s*(\d+)\s*-\s*(\d+)\s*$/)
      if (m) {
        defaults[`${key}_MIN`] = Number(m[1])
        defaults[`${key}_MAX`] = Number(m[2])
      }
    }
  }
  return defaults
}
